import { useCallback, useMemo } from 'react';
import {
  getApplicableRecipes,
  isAutoRecipeId,
  isDeletedRecipe,
  isNotInSortedRecipes,
  isRecipeAndElementEqual,
  isRecipeCategoryPropertyValueRecordModified,
  resolveAutoRecipeId,
  updateRecipeFromElement,
} from '@shared/helpers/recipe.helpers';
import { Results } from '@shared/models/unit.model';
import { Recipe, RecipeID } from '@shared/models/recipe.model';
import { IElement, OneOfElements } from '@shared/models/project.model';
import {
  getAllRecipeElements,
  getElementById,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import { getItemById } from '@shared/helpers/array.helpers';
import { getSelectedVersion } from '@client/store/ui';
import { useProductsLookup } from '@client/store/product';
import {
  useRecipeLookup,
  useRecipes,
  useUpdateRecipe,
  getRecipes,
} from '@client/store/recipe/recipe.hook';
import { getProject, useProject } from '@client/store/project';
import { useApplyBaselineRecipeOverwrite } from '@client/hooks/use-apply-baseline-recipe-overwrite.hook';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { getExpressionVariablesRecord } from '@shared/helpers/expression-variables.helpers';
import { usePvgisEnvironmentHourData } from '@client/store/pvgis';
import { useDataTables } from '@features/data-table/store/data-table-store.hooks';
import type { ApplyProjectChangesOptions } from '@client/store/project/use-apply-project-changes.hook';
import { AMP_ELEMENT_APPLY_RECIPE } from '@shared/constants';
import { expandElements } from '@client/hooks/use-expand-elements.hooks';
import { useGenerateFallbackName } from '@client/hooks/use-element-name.hooks';
import {
  getRecipeContextElement,
  useRecipeContextElement,
} from '@client/hooks/use-recipe-context-element.hooks';
import {
  buildRecipeProjectChange,
  resolveRecipePreviewElement,
} from '@shared/helpers/recipe-apply-payload.helpers';
import { getFallbackName } from '@shared/helpers/project.helpers';

/**
 * When a newly created recipe is referenced it won't exist in sortedRecipes yet
 *
 * @returns the sorted recipes or an updated list of the recipes if the recipe is not among the sorted recipes
 */
export const useRefreshRecipes = (): ((
  recipeId: string | undefined,
) => Recipe[]) => {
  // Include soft-deleted recipes: applying/resolving must see every recipe a
  // tree can reference (incl. nested recipe-in-recipe) even if soft-deleted.
  const sortedRecipes = useRecipes(true);

  return useCallback(
    (recipeId: string | undefined) =>
      isNotInSortedRecipes(recipeId, sortedRecipes)
        ? getRecipes(true)
        : sortedRecipes,
    [sortedRecipes],
  );
};

/**
 * The recipe that should be applied if the user selects auto for the given element
 * @param element
 * @returns
 */
export const useSuggestedAutoRecipe = (element: OneOfElements | undefined) => {
  const recipeElement = useRecipeContextElement(
    isElement(element) ? element : undefined,
  );
  const recipe = useApplicableRecipes(recipeElement ?? element)[0];
  return recipe;
};

/**
 * Get the recipe currently used by the element (by auto or manual selection)
 * @param element
 * @returns
 */
export const useRecipe = (
  element: OneOfElements | undefined,
): Recipe | undefined => {
  const recipeElement = useRecipeContextElement(
    isElement(element) ? element : undefined,
  );
  const target = recipeElement ?? element;
  // Elements store the applied recipe on `recipe_id`; properties use
  // {@link ElementPropertySource.Recipe}. resolveAutoRecipeId only reads the latter.
  const id =
    resolveAutoRecipeId(target) ??
    (isElement(target) ? target.recipe_id : undefined);
  const lookup = useRecipeLookup();
  const auto = useSuggestedAutoRecipe(target);
  return useMemo(() => {
    if (id && lookup[id]) {
      return lookup[id];
    }
    return isAutoRecipeId(target) ? auto : undefined;
  }, [target, id, lookup, auto]);
};

/**
 * Modified state of an element relative to its recipe.
 *
 * `pending` while the connected data tables have not loaded yet: the comparison
 * enriches the recipe with those tables, so the result is genuinely unknown
 * until they arrive.
 */
export type RecipeModified =
  { status: 'pending' } | { status: 'ready'; isModified: boolean };

const RECIPE_MODIFIED_PENDING: RecipeModified = { status: 'pending' };

export const useRecipeIsModified = (
  element?: OneOfElements,
): RecipeModified => {
  const recipeElement = useRecipeContextElement(element);
  const recipe = useRecipe(element);
  const dataTables = useDataTables();

  return useMemo(() => {
    if (!recipe || !recipeElement)
      return { status: 'ready', isModified: false };
    if (!dataTables) return RECIPE_MODIFIED_PENDING;
    return {
      status: 'ready',
      isModified: !isRecipeAndElementEqual(recipe, recipeElement, dataTables),
    };
  }, [recipeElement, recipe, dataTables]);
};

export const useIsRecipeCategoryPropertyValueRecordModified = (
  element?: OneOfElements,
): boolean => {
  const recipeElement = useRecipeContextElement(element);
  const recipe = useRecipe(element);

  return useMemo(
    () =>
      recipe && recipeElement
        ? !isRecipeCategoryPropertyValueRecordModified(recipeElement, recipe)
        : false,
    [recipeElement, recipe],
  );
};

/**
 * Return all applicable recipes for the given element ordered by co2
 * @param element
 * @param groupUnmappedRecipesInOther If true all recipes that can't be mapped to a enabled category will be grouped in the "Other" category
 * @returns
 */
export const useApplicableRecipes = (
  element?: OneOfElements,
  groupUnmappedRecipesInOther = false,
): Recipe[] => {
  const recipes = useRecipes();
  const recipeElement = useRecipeContextElement(
    isElement(element) ? element : undefined,
  );
  const target = recipeElement ?? element;

  return useMemo(
    // `recipes` already excludes soft-deleted recipes (useRecipes default),
    // so the picker never offers one.
    () => getApplicableRecipes(recipes, target, groupUnmappedRecipesInOther),
    [recipes, target, groupUnmappedRecipesInOther],
  );
};

export type ApplyRecipeOptions = {
  /** When true, skips the undo snackbar (e.g. clearing a link during recipe delete). */
  silent?: boolean;
};

/**
 * Apply a recipe to an element by id. Handles fallback naming, expansion, and undo snackbar.
 *
 * Builds a single {@link ProjectChange} via {@link buildRecipeProjectChange} and hands it to
 * {@link useApplyProjectChanges}, which routes it to baseline (no proposal selected) or records it
 * as a proposal action. The regenerated fallback name is baked onto the change, so it persists with
 * the structural apply rather than being applied separately.
 */
export const useApplyRecipe = () => {
  const applyChanges = useApplyProjectChanges();
  const selectedProposalId = useSelectedProposalId();
  const refreshRecipes = useRefreshRecipes();
  const generateFallbackName = useGenerateFallbackName();
  const productsLookup = useProductsLookup();
  const project = useProject();
  const weatherHours = usePvgisEnvironmentHourData();
  const dataTables = useDataTables();

  return useCallback(
    async (
      elementRef: IElement,
      recipeId: RecipeID,
      options?: ApplyRecipeOptions,
    ) => {
      const version = getSelectedVersion(true);
      // Callers may pass a proposal-resolved element; persist/preview always use baseline.
      const baselineFromVersion = getElementById(version, elementRef.id, false);
      const baselineElement = isElement(baselineFromVersion)
        ? baselineFromVersion
        : elementRef;
      const contextElement = getRecipeContextElement(baselineElement);
      const recipes = refreshRecipes(recipeId);

      // Soft-deleted recipes are hidden from every picker, so applying one means
      // a bug upstream (e.g. a stale list). Already-applied references still
      // resolve, but selecting a deleted recipe must never happen.
      const targetRecipe = getItemById(recipes, recipeId, false);
      if (targetRecipe && isDeletedRecipe(targetRecipe)) {
        throw new Error(
          `Cannot apply soft-deleted recipe "${targetRecipe.name}" (${recipeId})`,
        );
      }

      const products = { ...productsLookup, ...version.products };

      const activeProject = project ?? getProject();
      const variablesRecord = activeProject
        ? getExpressionVariablesRecord(
            activeProject,
            version,
            weatherHours ?? [],
            undefined,
            dataTables,
          )
        : {};
      const applyOptions = {
        parentPath: [version, contextElement],
        variablesRecord,
      };

      // Preview/persist parity: overwrite runs on baseline; proposal resolve replays there.
      const overwriteMode = selectedProposalId ? 'proposal' : 'baseline';
      const previewElement = resolveRecipePreviewElement(
        recipes,
        products,
        baselineElement,
        recipeId,
        applyOptions,
        overwriteMode,
      );

      // Recipe applies regenerate + persist the fallback name; detach/clear keep the existing name.
      const isApply = recipeId !== 'detach' && recipeId !== 'none';
      const fallbackName = isApply
        ? generateFallbackName(previewElement)
        : undefined;

      const { change } = buildRecipeProjectChange({
        elementId: contextElement.id,
        recipeId,
        recipes,
        products,
        contextElement,
        previewElement,
        selectedProposalId: selectedProposalId ?? undefined,
        applyOptions,
        fallbackName,
      });

      // Reflect the persisted name on the preview so UI and data match.
      const shouldRename =
        !!change &&
        fallbackName !== undefined &&
        getFallbackName(previewElement) !== fallbackName;
      const namedPreview = shouldRename
        ? { ...previewElement, fallbackName }
        : previewElement;

      if (namedPreview.elements.length) expandElements(namedPreview);

      if (!change) return namedPreview;

      const applyOptionsOverrides: ApplyProjectChangesOptions = {
        ...(!options?.silent && {
          snackbar: {
            type: 'undo',
            message: getRecipeUndoSnackbarMessage(recipeId),
          },
        }),
        ...(recipeId !== 'detach' &&
          recipeId !== 'none' && {
            amplitude: {
              eventType: AMP_ELEMENT_APPLY_RECIPE,
              eventProperties: { RecipeId: recipeId },
            },
          }),
      };

      await applyChanges([change], applyOptionsOverrides);

      return namedPreview;
    },
    [
      refreshRecipes,
      generateFallbackName,
      productsLookup,
      applyChanges,
      selectedProposalId,
      project,
      weatherHours,
      dataTables,
    ],
  );
};

const getRecipeUndoSnackbarMessage = (recipeId: RecipeID): string => {
  if (recipeId === 'detach') return 'Recipe detached';
  if (recipeId === 'none') return 'Recipe cleared';
  return 'Recipe applied';
};

/**
 * Reset element to match its recipe on the baseline tree (not proposal-scoped).
 */
export const useResetRecipe = (): ((
  element: IElement,
  recipe: Recipe,
) => Promise<void>) => {
  const applyBaselineRecipeOverwrite = useApplyBaselineRecipeOverwrite();

  return useCallback(
    async (element: IElement, recipe: Recipe) => {
      await applyBaselineRecipeOverwrite([{ elementId: element.id, recipe }], {
        snackbar: {
          type: 'undo',
          message: 'Recipe reset',
        },
      });
    },
    [applyBaselineRecipeOverwrite],
  );
};

export const useAllRecipeElementsInProject = (recipe?: Recipe): IElement[] => {
  const project = useProject();

  return useMemo(
    () => getAllRecipeElements(recipe, project),
    [project, recipe],
  );
};

export const useUpdateRecipeFromElement = (): ((
  recipe: Recipe,
  element: IElement,
  elementResults?: Results,
) => Promise<Recipe>) => {
  const updateRecipe = useUpdateRecipe();

  return useCallback(
    async (recipe: Recipe, element: IElement, elementResults: Results = {}) => {
      const updatedRecipeFromElement = updateRecipeFromElement(
        recipe,
        element,
        elementResults,
      );

      return updateRecipe(updatedRecipeFromElement);
    },
    [updateRecipe],
  );
};
